import { Fragment, useCallback } from 'react';

import uniqBy from 'lodash-es/uniqBy';
import { CircleDotIcon, CrosshairIcon, WorkflowIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router';
import { toast } from 'sonner';

import useBrains from '@/hooks/useBrains';
import useDialogs from '@/hooks/useDialogs';
import { cn } from '@/lib/utils';
import { useTurnBrainId } from '@/modules/tryIt/hooks/useTurnBrainId';
import {
  selectSelectedTurnIndex,
  selectSessionNodes,
  selectTryItSessionId,
} from '@/modules/tryIt/redux/selectors';
import { getNodeDetailsForSection } from '@/modules/tryIt/utils/helper';
import { popModal } from '@/redux/modals/actions';
import { requestNodeSelection } from '@/redux/nodes/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { LogSessionContentRow } from '@/shared/types/analytics';
import {
  AmendPayload,
  DebugStepSubType,
  Entity,
  NodeType,
  UserMessage,
} from '@/shared/types/tryIt';

import { BrainSteps } from './BrainSteps';
import { StartingNodeStep } from './StartingNodeStep';
import { PossibleMatchedIntents } from '../../../Messages/PossibleMatchedIntents/PossibleMatchedIntents';
import { StandaloneWrapper } from '../StandaloneWrapper';
import { ToolCallsSection } from '../ToolCallsSection';

import styles from '../DetailsSection.module.scss';

// Only the two fields read below are used — keeping the type honest lets
// simulation replay pass a minimal, possibly-null object without casting
// through the full row shape.
type BrainDetailsSectionProps = {
  content?: Partial<Pick<LogSessionContentRow, 'session_id'>> & { brain_parent_id?: string | null };
  simulation?: AmendPayload['simulation'];
};

export type BrainDetailsSectionContentProps = {
  slug: string;
  nodes: NodeType[] | Partial<NodeType>[];
  index: number;
  handleGoToNode: (id: string) => void;
  closeModal: () => void;
  session_id?: string;
  standAloneQuestion?: string;
  brainId?: string;
  simulation?: AmendPayload['simulation'];
};

export const BrainDetailsSectionContent = ({
  slug,
  nodes,
  index,
  handleGoToNode,
  closeModal,
  session_id,
  brainId,
  simulation,
}: BrainDetailsSectionContentProps) => {
  const { t } = useTranslation();
  const tryItSessionId = useSelector(selectTryItSessionId);
  const node = nodes[index];

  const {
    intent,
    intents,
    userMessage: nodeUserMessage,
    isPlannerIntentOfftopic,
    plannerIntent,
    isStandaloneAmended,
    isIntentAmended,
    showAddTrainingPhrase,
  } = getNodeDetailsForSection(node);

  const isFlowResponse = (node?.messages?.[0] as UserMessage)?.isFlowResponse;
  const reminder = (node?.messages?.[0] as UserMessage)?.reminder;

  // Determine the appropriate user message based on trigger type
  const userMessage =
    nodeUserMessage ||
    (isFlowResponse ? t('conversation.flow_completed') : null) ||
    (reminder ? t('try_it.reminder_triggered') : null) ||
    '';

  const entities = uniqBy(
    (node?.messages[0] as UserMessage)?.entities,
    'entity'
  ) as unknown as Entity[];
  const isRequisite = node?.steps && node?.steps?.[0]?.type !== 4 && !isFlowResponse;

  const triggeredNode = node?.nodes_stack?.find((n) => n.node_id === node?.steps?.[0]?.node_id);
  const brainSteps = !isRequisite && !isFlowResponse ? node?.steps?.slice(1) : node?.steps;
  const isWorkflowTriggered = node?.steps?.[0]?.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED;
  // The starting-node step is the node-entry step (steps[0]), sliced off the body list
  // like any node entry. Surface it as a dedicated row instead of the generic
  // node-triggered banner, since "Conversation starts at X" tells the whole story.
  const isStartingNode = node?.steps?.[0]?.sub_type === DebugStepSubType.STARTING_NODE;

  const { brain } = useBrains(brainId);

  const amendPayload: AmendPayload = {
    request_id: node.request_id || node.nodes_stack[0]?.request_id,
    task: 'standalone',
    session_id: session_id || tryItSessionId,
    language: brain?.language,
    simulation,
  };

  return (
    <div className="flex flex-col" data-testid="newDebugLogs">
      <StandaloneWrapper
        userMessage={userMessage}
        amendPayload={amendPayload}
        isAmended={isStandaloneAmended}
      />
      {entities.length > 0 && (
        <div className={styles.step}>
          <div className={styles.title}>
            <CrosshairIcon size={16} className="text-muted-foreground" />
            <div className="text-sm leading-5 font-normal text-muted-foreground">
              {t('try_it.entity', { count: entities?.length })}{' '}
              {entities?.map((entity, index) => (
                <Fragment key={entity.value}>
                  <NavLink
                    to={`/${slug}/ai-agents/${brainId}/workflows/entities/${entity.entity}`}
                    className={styles.link}
                    onClick={closeModal}
                  >
                    {entity.entity}
                  </NavLink>
                  {index < entities.length - 1 && ', '}
                </Fragment>
              ))}{' '}
              {t('try_it.matched')}
            </div>
          </div>
          <div className={styles.details}></div>
        </div>
      )}
      {isFlowResponse && triggeredNode && (
        <div className={styles.step}>
          <div className={styles.title}>
            <WorkflowIcon size={16} className="text-muted-foreground" />
            <div className="text-sm leading-5 font-normal text-muted-foreground">
              {t('common.node')}{' '}
              <button
                type="button"
                className={cn(
                  'inline border-0 bg-transparent p-0 text-sm leading-5 font-normal text-primary',
                  styles.link
                )}
                onClick={() => handleGoToNode(triggeredNode.node_id)}
              >
                {triggeredNode?.name}
              </button>{' '}
              {t('try_it.triggered')}
            </div>
          </div>
        </div>
      )}
      {!isRequisite && (
        <>
          <div className={styles.step}>
            <PossibleMatchedIntents
              key={amendPayload.request_id}
              brainId={brainId}
              isPlannerIntentOfftopic={isPlannerIntentOfftopic}
              intentName={intent?.intent}
              plannerIntent={plannerIntent}
              possibleMatchedIntents={intents}
              showAddTrainingPhrase={showAddTrainingPhrase}
              userMessage={userMessage}
              amendPayload={amendPayload}
              isAmended={isIntentAmended}
            />
          </div>
          {isStartingNode && triggeredNode && (
            <StartingNodeStep
              nodeName={triggeredNode.name}
              onGoToNode={() => handleGoToNode(triggeredNode.node_id)}
            />
          )}
          {!isStartingNode && brainSteps?.length > 0 && (
            <div className={styles.step}>
              <div className={styles.title}>
                <CircleDotIcon size={16} className="text-muted-foreground" />
                <div className="text-sm leading-5 font-normal text-muted-foreground">
                  {t('common.node')}{' '}
                  <button
                    type="button"
                    className={cn(
                      'inline border-0 bg-transparent p-0 text-sm leading-5 font-normal text-primary',
                      styles.link
                    )}
                    onClick={() => handleGoToNode(triggeredNode.node_id)}
                  >
                    {triggeredNode?.name}
                  </button>{' '}
                  {t('try_it.triggered')}
                  {isWorkflowTriggered && (
                    <div className="flex gap-1 py-1">
                      <div className="text-sm leading-5 font-normal text-muted-foreground">
                        ↪ {t('try_it.triggered_due_to')}
                      </div>
                      <span className="text-sm leading-5 font-medium text-foreground">
                        {t('try_it.ai_agent_using_guidelines')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.details}></div>
            </div>
          )}
        </>
      )}

      {node?.tool_calls?.tool_invocations?.length > 0 && brainId && (
        <div className={styles.step}>
          <ToolCallsSection toolInvocations={node.tool_calls.tool_invocations} brainId={brainId} />
        </div>
      )}

      <BrainSteps steps={brainSteps} />
    </div>
  );
};

export const BrainDetailsSection = ({ content, simulation }: BrainDetailsSectionProps) => {
  const navigate = useNavigate();
  const index = useSelector(selectSelectedTurnIndex);
  const nodes = useSelector(selectSessionNodes);

  const { session_id, brain_parent_id: logBrainParentId } = content || {};

  const brainId = useTurnBrainId(logBrainParentId);
  const { dialogs } = useDialogs(brainId);
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const closeModal = useCallback(() => {
    dispatch(popModal());
  }, [dispatch]);

  const handleGoToNode = useCallback(
    (id: string) => {
      const dialog = dialogs?.find((d) => d.nodes.some((n) => n.node_id === id));

      // If the dialog is deleted, we show an error message
      if (!dialog?.dialog_id) {
        toast.error(t('errors.deleted_dialog'));
        return;
      }

      // We get error here
      navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialog.dialog_id}`);

      closeModal();
      dispatch(requestNodeSelection(id));
    },
    [brainId, closeModal, dialogs, dispatch, navigate, slug, t]
  );

  return (
    <BrainDetailsSectionContent
      slug={slug}
      nodes={nodes}
      index={index}
      handleGoToNode={handleGoToNode}
      closeModal={closeModal}
      session_id={session_id}
      brainId={brainId}
      simulation={simulation}
    />
  );
};
